"use client";

import { Drawer } from "@base-ui/react/drawer";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";

import { BottomSheet } from "@/components/bottom-sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatPriceCompact } from "@/domain/pricing";
import { LINE_MAX_LENGTH } from "@/domain/validation";
import { cn } from "@/lib/utils/cn";

import { isValidPhoneStickerNumber } from "./phone-sticker";
import { isPhoneUpsellLocked, type PhoneUpsellStep } from "./phone-upsell-gate-context";

interface PhoneUpsellDrawerProps extends PhoneUpsellStep {
  open: boolean;
}

/**
 * The phone-number sticker offer every cart-link click goes through
 * (spec #107). The same bottom sheet as the configurator's added-to-cart
 * popup, with the ladder price leading the same way. From that popup it is
 * the popup's own second step instead (`PhoneUpsellContent`).
 */
export function PhoneUpsellDrawer({ open, ...step }: PhoneUpsellDrawerProps) {
  const t = useTranslations("pages.cart.phoneUpsell");
  const locked = isPhoneUpsellLocked(step.status);

  return (
    <BottomSheet
      open={open}
      onOpenChange={(next) => {
        if (!next && !locked) {
          step.onDismiss();
        }
      }}
      closeLabel={t("close")}
      showCloseButton={!locked}
    >
      <PhoneUpsellContent {...step} />
    </BottomSheet>
  );
}

interface PhoneUpsellContentProps extends Omit<PhoneUpsellStep, "onDismiss"> {
  /** Focus the title on mount: the step replaced what had focus. */
  focusTitle?: boolean;
  className?: string;
}

/**
 * The offer's title, price and number form, for a `BottomSheet`. Renders the
 * sheet's `Drawer.Title` and `Drawer.Description`.
 *
 * PII: the input is masked in session replay by `maskAllInputs` (and `tel`
 * is pinned in `maskInputOptions`); its container is also blocked with
 * `data-ph-no-capture`, per `docs/observability.md`. The number never leaves
 * this component except through `onAccept`.
 */
export function PhoneUpsellContent({
  status,
  offerPrice,
  offerSavings,
  onAccept,
  onDecline,
  focusTitle = false,
  className,
}: PhoneUpsellContentProps) {
  const t = useTranslations("pages.cart.phoneUpsell");
  const inputId = useId();
  const errorId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showInvalid, setShowInvalid] = useState(false);

  const locked = isPhoneUpsellLocked(status);

  useEffect(() => {
    if (focusTitle) {
      titleRef.current?.focus({ preventScroll: true });
    }
  }, [focusTitle]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (locked) {
      return;
    }
    if (!isValidPhoneStickerNumber(phoneNumber)) {
      setShowInvalid(true);
      return;
    }
    onAccept(phoneNumber);
  }

  const errorMessage = showInvalid ? t("invalid") : status === "add_failed" ? t("addError") : null;

  return (
    <div className={cn("flex w-full flex-col items-center gap-5", className)}>
      <div className="flex flex-col items-center gap-2 pt-2">
        <Drawer.Title
          ref={titleRef}
          tabIndex={focusTitle ? -1 : undefined}
          className="font-heading text-2xl leading-tight text-balance outline-none"
        >
          {t("title")}
        </Drawer.Title>
        <Drawer.Description className="max-w-xs text-sm text-ink-2">{t("body")}</Drawer.Description>
      </div>

      <div className="flex flex-col items-center gap-1 py-5">
        <span className="flex items-baseline gap-3">
          <span className="font-heading text-5xl leading-none text-sale">
            {formatPriceCompact(offerPrice)}
          </span>
          {offerSavings > 0 ? (
            <span className="text-lg text-ink-3 line-through">
              {formatPriceCompact(offerPrice + offerSavings)}
            </span>
          ) : null}
        </span>
        {offerSavings > 0 ? (
          <span className="mt-1 text-sm font-semibold text-ink">
            {t("saving", { amount: formatPriceCompact(offerSavings) })}
          </span>
        ) : null}
      </div>

      <form className="flex w-full flex-col gap-3 text-left" onSubmit={handleSubmit} noValidate>
        <div className="flex flex-col gap-2" data-ph-no-capture>
          <label htmlFor={inputId} className="text-sm font-medium text-ink">
            {t("inputLabel")}
          </label>
          <Input
            id={inputId}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            // One past the line limit so an over-long paste shows the error
            // instead of being silently truncated into a different number.
            maxLength={LINE_MAX_LENGTH + 1}
            placeholder={t("placeholder")}
            value={phoneNumber}
            disabled={locked}
            onChange={(event) => {
              setPhoneNumber(event.target.value);
              setShowInvalid(false);
            }}
            aria-invalid={showInvalid || undefined}
            aria-describedby={errorMessage ? errorId : undefined}
            className="h-12 rounded-2xl px-4 text-lg tabular-nums"
          />
        </div>
        {errorMessage ? (
          <p id={errorId} role="alert" className="text-xs text-destructive">
            {errorMessage}
          </p>
        ) : null}
        <div className="mt-1 flex flex-col gap-2">
          <Button type="submit" size="lg" className="w-full" disabled={locked}>
            {status === "adding" ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t("adding")}
              </>
            ) : (
              t("accept")
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full"
            disabled={locked}
            onClick={onDecline}
          >
            {t("decline")}
            {status === "continuing" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <span aria-hidden>→</span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
